import React, { useCallback, useMemo, useEffect, useState } from 'react';
import {
    DndContext,
    DragOverlay,
    useSensor,
    useSensors,
    TouchSensor,
    MouseSensor,
    useDraggable,
    useDroppable,
    DragEndEvent,
    UniqueIdentifier,
} from '@dnd-kit/core';
import { QuestionTypeConnect } from "../../types/gameSession";
import { useAppDispatch, useAppSelector } from "../../app/store";
import { updateConnectSelectedPairs } from "../../app/slices/gameSession";

type Props = {
    question: QuestionTypeConnect;
};

type DraggableItemProps = {
    id: string;
    option: {
        option: string;
        correct: boolean;
        index: number;
    };
    displayCorrectAnswers: boolean;
};

const DraggableItem = React.memo(({ id, option, displayCorrectAnswers }: DraggableItemProps) => {
    const { attributes, listeners, setNodeRef, isDragging, transform } = useDraggable({
        id: id,
    });

    const isClone = Boolean(transform);

    return (
        <div
            ref={setNodeRef}
            {...listeners}
            {...attributes}
            className={`cloud-item 
                ${isDragging ? 'dragging' : ''} 
                ${isClone ? 'dragging-source' : ''}
                ${displayCorrectAnswers ? (option.correct ? 'correct' : 'incorrect') : ''}`}
        >
            {option.option}
        </div>
    );
});

DraggableItem.displayName = 'DraggableItem';

const DroppableContainer = React.memo(({
    id,
    children,
    className
}: {
    id: string;
    children: React.ReactNode;
    className?: string;
}) => {
    const { setNodeRef, isOver } = useDroppable({
        id: id,
    });

    return (
        <div
            ref={setNodeRef}
            className={`${className || ''} ${isOver ? 'dragging-over' : ''}`}
        >
            {children}
        </div>
    );
});

DroppableContainer.displayName = 'DroppableContainer';

const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkIfMobile();
        const handleResize = () => {
            window.requestAnimationFrame(checkIfMobile);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return isMobile;
};

const QuestionConnect: React.FC<Props> = ({ question }) => {
    const dispatch = useAppDispatch();
    const { data } = useAppSelector((state) => state.game);
    const displayCorrectAnswers = data?.display_correct_answers || false;
    const isMobile = useIsMobile();
    const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);

    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 0,
                tolerance: 5,
            },
        })
    );


    const getSelectedPair = useCallback((option1Index: number) => {
        return question.selected_pairs?.find(pair => pair[0] === option1Index)?.[1] ?? null;
    }, [question.selected_pairs]);

    const getOption2Pair = useCallback((option2Index: number) => {
        return question.selected_pairs?.find(pair => pair[1] === option2Index)?.[0] ?? null;
    }, [question.selected_pairs]);

    const displayedOptions1 = useMemo(() => {
        if (!isMobile) return question.options1;

        return [...question.options1].sort((a, b) => {
            const aPaired = getSelectedPair(a.index) !== null;
            const bPaired = getSelectedPair(b.index) !== null;
            return (!aPaired && bPaired) ? -1 : (aPaired && !bPaired) ? 1 : 0;
        });
    }, [question.options1, getSelectedPair, isMobile]);

    const availableOptions = useMemo(() =>
        question.options2.filter(option => getOption2Pair(option.index) === null),
    [question.options2, getOption2Pair]);

    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const { active, over } = event;
        setActiveId(null);

        if (!over) return;

        const option2Id = active.id.toString();
        const containerId = over.id.toString();

        if (!option2Id.startsWith('option2-') || !containerId.startsWith('container-')) return;

        const option2Index = parseInt(option2Id.split('-')[1]);
        const option1Index = parseInt(containerId.split('-')[1]);

        const filteredPairs = question.selected_pairs?.filter(pair =>
            pair[0] !== option1Index && pair[1] !== option2Index
        ) || [];

        const updatedPairs: [number, number][] = [
            ...filteredPairs,
            [option1Index, option2Index] as [number, number]
        ];

        dispatch(updateConnectSelectedPairs({ selected_pairs: updatedPairs }));
    }, [dispatch, question.selected_pairs]);

    const handleContainerClick = useCallback((option1Index: number, option2Index: number) => {
        const updatedPairs = question.selected_pairs?.filter(pair =>
            !(pair[0] === option1Index && pair[1] === option2Index)
        ) || [] as [number, number][];
        dispatch(updateConnectSelectedPairs({ selected_pairs: updatedPairs }));
    }, [dispatch, question.selected_pairs]);

    const handleDragStart = useCallback((event: DragEndEvent) => {
        setActiveId(event.active.id);
    }, []);

    const activeOption = useMemo(() => {
        if (!activeId || typeof activeId !== 'string') return null;
        const optionIndex = parseInt(activeId.split('-')[1]);
        return question.options2.find(opt => opt.index === optionIndex) || null;
    }, [activeId, question.options2]);

    return (
        <DndContext
            onDragEnd={handleDragEnd}
            onDragStart={handleDragStart}
            sensors={sensors}
        >
            <div className="connect-question-container">
                <DroppableContainer id="cloud" className="cloud-wrapper">
                    {availableOptions.map((option) => (
                        <DraggableItem
                            key={option.index}
                            id={`option2-${option.index}`}
                            option={option}
                            displayCorrectAnswers={displayCorrectAnswers}
                        />
                    ))}
                </DroppableContainer>

                <div className="containers-grid">
                    {displayedOptions1.map((option1) => {
                        const pairedOption2Index = getSelectedPair(option1.index);
                        const pairedOption2 = pairedOption2Index !== null
                            ? question.options2.find(opt => opt.index === pairedOption2Index)
                            : null;

                        return (
                            <DroppableContainer
                                key={option1.index}
                                id={`container-${option1.index}`}
                                className={`option-container 
                                    ${pairedOption2 ? 'paired' : ''} 
                                    ${displayCorrectAnswers ? (option1.correct ? 'correct' : 'incorrect') : ''}`}
                            >
                                <div
                                    className="connection-content"
                                    onClick={() => pairedOption2 && handleContainerClick(option1.index, pairedOption2.index)}
                                >
                                    <div className="option1-text">
                                        {option1.option}
                                    </div>

                                    <div className={`paired-content ${pairedOption2 ? 'visible' : ''}`}>
                                        {pairedOption2 && (
                                            <div className="option2-text">
                                                {pairedOption2.option}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </DroppableContainer>
                        );
                    })}
                </div>

                <DragOverlay>
                    {activeOption && (
                        <div className="cloud-item dragging">
                            {activeOption.option}
                        </div>
                    )}
                </DragOverlay>

            </div>
        </DndContext>
    );
};

export default React.memo(QuestionConnect);